import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationContext = createContext(null);

// VAPID public key for push notifications (you need to generate this)
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  if (!base64String) return new Uint8Array();
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  
  // Use ref for interval to avoid dependency issues
  const intervalRef = useRef(null);

  // Check push notification support
  useEffect(() => {
    const checkPushSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setPushSupported(supported);
      
      if (supported && navigator.serviceWorker.controller) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setPushEnabled(!!subscription);
        } catch (err) {
          logger.error('Error checking push subscription:', err);
        }
      }
    };
    checkPushSupport();
  }, []);

  // Fetch notifications function - memoized with useCallback
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${API}/notifications/${user.id}`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      // Silently fail - notifications are not critical
      logger.error('Failed to fetch notifications:', err);
    }
  }, [user?.id]);

  // Fetch notifications when user changes
  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      
      // Poll for new notifications every 30 seconds
      intervalRef.current = setInterval(fetchNotifications, 30000);
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user?.id, fetchNotifications]);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now().toString(),
      ...notification,
      created_at: new Date().toISOString(),
      is_read: false
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    
    // Show toast
    toast(notification.title, {
      description: notification.message,
      action: notification.action
    });
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      logger.error('Failed to mark notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      await axios.put(`${API}/notifications/${user.id}/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      logger.error('Failed to mark all notifications as read:', err);
    }
  }, [user?.id]);

  const archiveNotification = useCallback(async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      logger.error('Failed to archive notification:', err);
    }
  }, []);

  // Push notification functions
  const subscribeToPush = useCallback(async () => {
    if (!pushSupported || !user?.id) return false;
    
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Push notifications permission denied');
        return false;
      }
      
      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      
      // Send subscription to backend
      const subscriptionJSON = subscription.toJSON();
      await axios.post(`${API}/push/subscribe?user_id=${user.id}`, {
        endpoint: subscriptionJSON.endpoint,
        p256dh_key: subscriptionJSON.keys?.p256dh || '',
        auth_key: subscriptionJSON.keys?.auth || '',
        user_agent: navigator.userAgent
      });
      
      setPushEnabled(true);
      toast.success('Push notifications enabled!');
      return true;
    } catch (err) {
      logger.error('Failed to subscribe to push:', err);
      toast.error('Failed to enable push notifications');
      return false;
    }
  }, [pushSupported, user?.id]);

  const unsubscribeFromPush = useCallback(async () => {
    if (!pushSupported) return;
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);
      }
      
      setPushEnabled(false);
      toast.success('Push notifications disabled');
    } catch (err) {
      logger.error('Failed to unsubscribe from push:', err);
    }
  }, [pushSupported]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    refreshNotifications: fetchNotifications,
    pushSupported,
    pushEnabled,
    subscribeToPush,
    unsubscribeFromPush
  }), [
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    fetchNotifications,
    pushSupported,
    pushEnabled,
    subscribeToPush,
    unsubscribeFromPush
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
